import {
  EliteLandingPageState,
  type EliteOnboardingEligiblePlan,
  type EliteOnboardingStatusResponse,
  type EliteSubscriptionUpgradePricingDetails
} from '../../types/misc.types'
import { getEligiblePlanCtaAmount } from '../../components/elite/dashboard/eliteOnboardingFocusPlan'
import { isPriceBasedOfferType, isTenureBasedOfferType } from '../../components/elite/EliteSpecialOfferGiftBanner'
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import {
  newEliteLandingPlanStateConfig,
  resolveSelectedPlanForCta
} from '../new-elite-landing/components/NewEliteLandingPlanSelector'
import { elitePlansLandingStateConfig, type ElitePlansLifecycleUi } from './elitePlansLandingState'
import type { ElitePlansAdvisorySelection } from './hooks/useElitePlansAdvisorySelection'
import type { PlanConfig } from './types/plan.types'
import type {
  ElitePlansPriceBasedOfferDisplay,
  ElitePlansPrimaryCtaDisplay,
  ElitePlansTenureBasedOfferDisplay
} from './types/cta.types'
import {
  ELITE_CTA_PREFIX,
  ELITE_FEE_SUFFIX_MONTHLY,
  ELITE_FEE_SUFFIX_YEARLY,
  ELITE_PRIME_CTA_PREFIX,
  ELITE_PRIME_FEE_SUFFIX,
  ELITE_PRIME_OFFER_CTA_PREFIX,
  ELITE_PRIME_UPGRADE_CTA_LABEL
} from './constants/ctaCopy'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from './constants/planIds'
import { resolveElitePrimeUpgradeDetails } from './elitePlansUpgradeDetails'

export type {
  ElitePlansPrimaryCtaDisplay,
  ElitePlansPriceBasedOfferDisplay,
  ElitePlansTenureBasedOfferDisplay
} from './types/cta.types'

export function resolveRecommendedElitePrimePlan(
  status?: EliteOnboardingStatusResponse
): EliteOnboardingEligiblePlan | undefined {
  return status?.eligiblePlans?.find((entry) => entry?.recommended) ?? status?.eligiblePlans?.[0]
}

export function resolveElitePrimePriceOffPercent(plan?: EliteOnboardingEligiblePlan): number {
  const baseFees = plan?.basePlanFees?.amount ?? 0
  const effectiveFees = plan?.effectivePlanFees?.amount ?? 0
  if (baseFees <= 0 || effectiveFees >= baseFees) {
    return 0
  }
  return Math.round(((baseFees - effectiveFees) / baseFees) * 100)
}

export function resolveElitePrimeTenureExtraMonths(plan?: EliteOnboardingEligiblePlan): number {
  const effectiveValue = Number(plan?.effectivePlanTenure?.value ?? 0)
  const baseValue = Number(plan?.basePlanTenure?.value ?? 0)
  if (
    plan?.effectivePlanTenure?.period === 'MONTH' &&
    plan?.basePlanTenure?.period === 'MONTH' &&
    effectiveValue > baseValue
  ) {
    return effectiveValue - baseValue
  }
  const tenureDiff = plan?.tenureDiff
  if (typeof tenureDiff !== 'number' || tenureDiff <= 0) return 0
  if (tenureDiff <= 24) return tenureDiff
  return Math.max(1, Math.round(tenureDiff / 30))
}

function buildElitePrimeOfferCta(
  feeAmount: number
): Extract<ElitePlansPrimaryCtaDisplay, { variant: 'elite-prime-priced' }> {
  return buildElitePrimePricedCta(feeAmount, ELITE_PRIME_OFFER_CTA_PREFIX)
}

export function resolveElitePlansPriceBasedOfferDisplay(
  status?: EliteOnboardingStatusResponse
): ElitePlansPriceBasedOfferDisplay | null {
  const plan = resolveRecommendedElitePrimePlan(status)
  if (!isPriceBasedOfferType(plan?.offerType)) {
    return null
  }

  const feeAmount = plan?.effectivePlanFees?.amount
  if (feeAmount == null) {
    return null
  }

  const percentOff = resolveElitePrimePriceOffPercent(plan)
  if (percentOff <= 0) {
    return null
  }

  return {
    percentOff,
    cta: buildElitePrimeOfferCta(feeAmount)
  }
}

export function resolveElitePlansTenureBasedOfferDisplay(
  status?: EliteOnboardingStatusResponse
): ElitePlansTenureBasedOfferDisplay | null {
  const plan = resolveRecommendedElitePrimePlan(status)
  if (!isTenureBasedOfferType(plan?.offerType)) {
    return null
  }

  const feeAmount = plan?.effectivePlanFees?.amount
  if (feeAmount == null) {
    return null
  }

  const extraMonths = resolveElitePrimeTenureExtraMonths(plan)
  if (extraMonths <= 0) {
    return null
  }

  const monthsLabel = extraMonths === 1 ? '1 extra month' : `${extraMonths} extra months`

  return {
    extraMonths,
    offerText: `Get ${monthsLabel} of Elite Prime`,
    plusBannerSublabel: `1 year + ${extraMonths} month${extraMonths === 1 ? '' : 's'} free`,
    cta: buildElitePrimeOfferCta(feeAmount)
  }
}

export function resolveElitePlansPrimaryCtaDisplay({
  plan,
  status,
  lifecycle,
  advisory
}: {
  plan: PlanConfig
  status?: EliteOnboardingStatusResponse
  lifecycle: ElitePlansLifecycleUi
  advisory?: ElitePlansAdvisorySelection | null
}): ElitePlansPrimaryCtaDisplay {
  if (advisory) {
    return resolveEliteAdvisoryPrimaryCtaDisplay(advisory, status)
  }

  const landingPageState = status?.eliteLandingPageState
  const stateConfig = landingPageState ? (elitePlansLandingStateConfig[landingPageState] ?? {}) : {}
  const hasLifecycleCtaOverride = Boolean(stateConfig.ctaLabel)

  if (plan.id !== ELITE_PLANS_ELITE_PRIME_TAB_ID || hasLifecycleCtaOverride || !status) {
    return { variant: 'plain', label: lifecycle.ctaLabel }
  }

  const eligiblePlan = resolveRecommendedElitePrimePlan(status)
  const effectiveFees = eligiblePlan?.effectivePlanFees?.amount

  const elitePrimeUpgrade = resolveElitePrimeUpgradeDetails(status)
  const upgradePricing = elitePrimeUpgrade?.upgradePricingDetails
  if (upgradePricing) {
    return buildElitePrimePricedCta(resolveElitePrimeUpgradeCtaFeeAmount(effectiveFees, upgradePricing))
  }

  // Elite user with upgrade CTA but no upgrade pricing: keep upgrade wording (click posts upgrade payload)
  if (elitePrimeUpgrade) {
    const upgradeFeeAmount =
      elitePrimeUpgrade.upgradePlanDetails?.effectivePlanFees?.amount ?? effectiveFees
    if (upgradeFeeAmount == null) {
      return { variant: 'plain', label: ELITE_PRIME_UPGRADE_CTA_LABEL }
    }
    return buildElitePrimePricedCta(upgradeFeeAmount)
  }

  // Elite paid, eSign pending: user is on the way to Elite, so Prime reads as an upgrade
  if (landingPageState === EliteLandingPageState.ELITE_PAYMENT_COMPLETED_PRIME_PENDING && effectiveFees != null) {
    return buildElitePrimePricedCta(effectiveFees)
  }

  const priceBasedOffer = resolveElitePlansPriceBasedOfferDisplay(status)
  if (priceBasedOffer) {
    return priceBasedOffer.cta
  }

  const tenureBasedOffer = resolveElitePlansTenureBasedOfferDisplay(status)
  if (tenureBasedOffer) {
    return tenureBasedOffer.cta
  }

  if (effectiveFees == null) {
    return { variant: 'plain', label: lifecycle.ctaLabel }
  }

  return buildElitePrimeOfferCta(effectiveFees)
}

/** Elite tab: state overrides keep plain copy; default purchase uses live plan fees. */
function resolveEliteAdvisoryPrimaryCtaDisplay(
  advisory: ElitePlansAdvisorySelection,
  status?: EliteOnboardingStatusResponse
): ElitePlansPrimaryCtaDisplay {
  const landingPageState = status?.eliteLandingPageState
  const stateConfig = landingPageState ? (newEliteLandingPlanStateConfig[landingPageState] ?? {}) : {}
  const hasStateCtaOverride = Boolean(
    stateConfig.ctaLabel || stateConfig.ctaLabelByPlan || stateConfig.useRenewCtaLabel
  )
  if (hasStateCtaOverride) {
    return { variant: 'plain', label: advisory.ctaLabel }
  }

  const eligiblePlan = resolveSelectedPlanForCta(advisory.eligiblePlans, advisory.selectedTenure)
  if (eligiblePlan == null) {
    return { variant: 'plain', label: advisory.ctaLabel }
  }

  const feeAmount = getEligiblePlanCtaAmount(eligiblePlan)
  if (feeAmount <= 0) {
    return { variant: 'plain', label: advisory.ctaLabel }
  }

  const price = roundOffAndFormatNumberWithCurrency(feeAmount)
  const feeSuffix =
    advisory.selectedTenure === 'yearly' ? ELITE_FEE_SUFFIX_YEARLY : ELITE_FEE_SUFFIX_MONTHLY
  const prefix = ELITE_CTA_PREFIX
  return {
    variant: 'elite-prime-priced',
    label: `${prefix}${price}${feeSuffix}`,
    prefix,
    price,
    feeSuffix
  }
}

/**
 * Upgrade display price: when current-plan credit exists, show effective fees minus that
 * adjustment; otherwise the full upgrade plan price.
 * Used by the hero CTA and the Elite Prime plus-banner row.
 */
export function resolveElitePrimeUpgradeCtaFeeAmount(
  effectiveFees: number | undefined,
  upgradePricing: EliteSubscriptionUpgradePricingDetails
): number {
  const adjustment = upgradePricing.adjustmentFromCurrentPlan
  if (adjustment != null && effectiveFees != null) {
    return effectiveFees - adjustment
  }
  return upgradePricing.upgradePlanPrice
}

function buildElitePrimePricedCta(
  feeAmount: number,
  prefix: string = ELITE_PRIME_CTA_PREFIX
): Extract<ElitePlansPrimaryCtaDisplay, { variant: 'elite-prime-priced' }> {
  const price = roundOffAndFormatNumberWithCurrency(feeAmount)
  const feeSuffix = ELITE_PRIME_FEE_SUFFIX
  return {
    variant: 'elite-prime-priced',
    label: `${prefix}${price}${feeSuffix}`,
    prefix,
    price,
    feeSuffix
  }
}
